import debounce from 'lodash/debounce'
import { useRouter } from 'next/router'
import { ReactNode, useEffect, useState } from 'react'

import Icon from '@/components/Icon'

import BEMHelper from '@/helpers/bem'
import styles from './Styles.module.scss'
const bem = BEMHelper(styles)

interface Props {
  loading: boolean
  defaultValue: string
  children?: ReactNode
  placeholder?: string
  small?: boolean
  suggestions?: string[]
}

export default function Search({
  loading,
  defaultValue,
  children,
  placeholder = 'Søk',
  small,
  suggestions,
}: Props) {
  const router = useRouter()
  const [routerReady, setRouterReady] = useState(false)
  const [currentSuggestion, setCurrentSuggestion] = useState(0)
  const wordInterval = 3000

  useEffect(() => {
    if (router.isReady) {
      setRouterReady(true)
    }
  }, [router.isReady])

  useEffect(() => {
    const interval = setInterval(() => {
      if (suggestions && suggestions.length > 0) {
        setCurrentSuggestion((current) => (current + 1 >= suggestions.length ? 0 : current + 1))
      }
    }, wordInterval)
    return () => clearInterval(interval)
  }, [suggestions])

  const updateUrl = debounce((q: string) => {
    if (q === '' || q === undefined || q === null) {
      delete router.query.q
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
          },
        },
        undefined,
        { shallow: true },
      )
    } else {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            q: q ? q : undefined,
          },
        },
        undefined,
        { shallow: true },
      )
    }
  }, 400)

  return (
    <form
      {...bem('', { loading, small })}
      onSubmit={(event) => {
        event.preventDefault()
        const formData = new FormData(event.target as HTMLFormElement)
        const searchValue = formData.get('search')
        if (searchValue !== null) {
          updateUrl(searchValue.toString())
        }
      }}
    >
      <div {...bem('wrapper')}>
        <input
          type="search"
          name="search"
          autoFocus
          autoComplete="off"
          placeholder={routerReady && !suggestions ? placeholder : ' '}
          spellCheck="false"
          onChange={(event) => {
            updateUrl(event.target.value)
          }}
          defaultValue={defaultValue}
          disabled={!routerReady}
          {...bem('input')}
        />

        <div {...bem('placeholders')} style={{ '--interval': `${wordInterval}ms` }}>
          <span>Leter du etter </span>
          <span {...bem('suggestions')}>
            {suggestions?.map((str, index) => (
              <span key={str} {...bem('suggestion', { active: currentSuggestion === index })}>
                {str}?
              </span>
            ))}
          </span>
        </div>

        <button type="submit" aria-label="Søk" {...bem('submit')}>
          <Icon icon="search" />
        </button>
      </div>
      {children && <div {...bem('filters', { small })}>{children}</div>}
    </form>
  )
}
